/**
 * La primera pregunta al crear algo que se vende: ¿qué es?
 *
 * ── Por qué existe ───────────────────────────────────────────────────────────
 * Había DOS botones de crear —«Nueva oferta» en el catálogo y «Nuevo servicio»
 * en Reservas— que escriben en la misma tabla con distintos valores por defecto.
 * Nadie sabe cuál pulsar hasta que ya pulsó, y quien acierta por casualidad se
 * entera de que existía el otro cuando ya tiene la oferta creada.
 *
 * Esto es una sola puerta que reparte. No crea nada: elige el camino y se
 * aparta. La reserva se va a su sección —allí vive el asistente que sabe pintar
 * una agenda— y lo demás abre el asistente de ofertas con el tipo ya puesto.
 *
 * ── Y el curso apagado ───────────────────────────────────────────────────────
 * Se enseña sin poder elegirse, a propósito. Es el único momento en que alguien
 * está buscando exactamente eso, así que decir «todavía no» aquí es informar; en
 * cualquier otro sitio del panel sería ruido. Y evita la pregunta de soporte más
 * cara de todas: «¿puedo vender cursos con esto?».
 */
import { createPortal } from 'react-dom'
import { useEffect } from 'react'
import {
  IconBriefcase,
  IconCalendarEvent,
  IconChevronRight,
  IconFileTypePdf,
  IconPackage,
  IconSchool,
  IconX,
} from '@tabler/icons-react'
import { AiMascot } from '@webbuilder/ui'
import { useI18n } from '../../../lib/i18n'

/** Lo que se puede elegir. `course` está en la lista y no se puede pulsar. */
export type NewOfferKind = 'digital' | 'physical' | 'booking' | 'service'

const CHOICES = [
  {
    kind: 'digital' as const,
    Icon: IconFileTypePdf,
    titleKey: 'offers.kind.digital',
    bodyKey: 'offers.newKind.digitalBody',
  },
  {
    kind: 'physical' as const,
    Icon: IconPackage,
    titleKey: 'offers.kind.physical',
    bodyKey: 'offers.newKind.physicalBody',
  },
  {
    kind: 'booking' as const,
    Icon: IconCalendarEvent,
    titleKey: 'offers.kind.booking',
    bodyKey: 'offers.newKind.bookingBody',
  },
  {
    kind: 'service' as const,
    Icon: IconBriefcase,
    titleKey: 'offers.kind.service',
    bodyKey: 'offers.newKind.serviceBody',
  },
]

export function NewOfferKindDialog({ onPick, onClose }: {
  onPick: (kind: NewOfferKind) => void
  onClose: () => void
}) {
  const { t } = useI18n()

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return createPortal(
    <div
      className="dash-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}
    >
      <section className="dash-ofk" role="dialog" aria-modal="true" aria-label={t('offers.newKind.title')}>
        <header className="dash-ofk-head">
          {/* La pregunta va dentro del globo, no suelta al lado.
              Con el texto a secas la mascota quedaba de adorno junto a un
              titular; con el pico apuntándola, la pregunta pasa a ser algo que
              alguien está diciendo, que es lo único que hace que valga la pena
              que esté. Es el mismo globo del saludo del Resumen — reusado, no
              copiado. */}
          <AiMascot />
          <div className="dash-mascot-bubble">
            <h2 className="dash-mascot-bubble-greeting">{t('offers.newKind.title')}</h2>
            <p className="dash-mascot-bubble-line">{t('offers.newKind.lead')}</p>
          </div>
          <button type="button" className="dash-create-close" aria-label={t('offers.new.close')} onClick={onClose}>
            <IconX size={18} />
          </button>
        </header>

        <div className="dash-ofk-grid">
          {CHOICES.map(({ kind, Icon, titleKey, bodyKey }) => (
            <button key={kind} type="button" className="dash-ofk-card" onClick={() => onPick(kind)}>
              <span className="dash-ofk-icon" aria-hidden="true"><Icon size={20} /></span>
              <strong>{t(titleKey)}</strong>
              <small>{t(bodyKey)}</small>
              <IconChevronRight className="dash-ofk-go" size={16} aria-hidden="true" />
            </button>
          ))}

          {/* A la vista y apagado: decir que existe y no dejar usarlo explica
              más que esconderlo, y éste es el segundo exacto en que alguien lo
              está buscando. */}
          <div className="dash-ofk-card is-soon" aria-disabled="true">
            <span className="dash-ofk-icon" aria-hidden="true"><IconSchool size={20} /></span>
            <strong>{t('offers.kind.course')}</strong>
            <small>{t('offers.newKind.courseBody')}</small>
            <span className="dash-ofk-soon">{t('offers.newKind.soon')}</span>
          </div>
        </div>
      </section>
    </div>,
    document.body,
  )
}
